import { useId, useMemo, useState } from 'react';
import type React from 'react';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxCollection,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxInput,
  ComboboxItem,
  ComboboxLabel,
  ComboboxList,
  ComboboxSeparator,
  ComboboxValue,
} from '@/components/ui/combobox';
import { Label } from '@/components/ui/label';

type ComboboxGroupedFieldOption = {
  label: string;
  value: string;
};

export type ComboboxGroupedFieldGroup = {
  key: string;
  label: string;
  items: ComboboxGroupedFieldOption[];
  disabled?: boolean;
};

type ComboboxGroupedFieldProps = {
  groups: ComboboxGroupedFieldGroup[];
  multiple?: boolean;
  label?: string;
  placeholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  value?: string | string[] | null;
  defaultValue?: string | string[] | null;
  onChange?: (value: string | string[] | null) => void;
};

export const ComboboxGroupedField: React.FC<ComboboxGroupedFieldProps> = ({
  groups,
  multiple = false,
  label,
  placeholder = 'Select...',
  emptyMessage = 'No items found.',
  disabled,
  value: valueProp,
  defaultValue,
  onChange,
}) => {
  const inputId = useId();
  const [internalValue, setInternalValue] = useState<string | string[] | null>(
    defaultValue ?? (multiple ? [] : null)
  );
  const isControlled = valueProp !== undefined;
  const value = isControlled ? valueProp : internalValue;

  const selectedOptions = useMemo(() => {
    const options = groups.flatMap((group) => group.items);
    if (multiple) {
      const values = Array.isArray(value) ? value : [];
      return options.filter((option) => values.includes(option.value));
    } else if (value === null) {
      return null;
    } else {
      return options.find((option) => option.value === value) ?? null;
    }
  }, [groups, value, multiple]);

  return (
    <div className="tw:flex tw:flex-col tw:gap-2">
      {label && <Label htmlFor={inputId}>{label}</Label>}
      <Combobox
        items={groups}
        value={selectedOptions}
        multiple={multiple}
        onValueChange={(nextValue) => {
          if (multiple) {
            const nextOptions = Array.isArray(nextValue) ? nextValue : [];
            const nextValues = nextOptions.map((option) => option.value);
            if (!isControlled) setInternalValue(nextValues);
            onChange?.(nextValues);
            return;
          }

          const option = nextValue as ComboboxGroupedFieldOption | null;
          const nextValueString = option?.value ?? null;
          if (!isControlled) setInternalValue(nextValueString);
          onChange?.(nextValueString);
        }}
        itemToStringLabel={(option) => option.label}
        itemToStringValue={(option) => option.value}
        isItemEqualToValue={(a, b) => a.value === b.value}
        disabled={disabled}
      >
        {multiple ? (
          <ComboboxChips>
            <ComboboxValue>
              {(selectedOptions as ComboboxGroupedFieldOption[]).map(
                (option) => (
                  <ComboboxChip key={option.value}>{option.label}</ComboboxChip>
                )
              )}
            </ComboboxValue>
            <ComboboxChipsInput
              id={inputId}
              placeholder={placeholder}
              disabled={disabled}
            />
          </ComboboxChips>
        ) : (
          <ComboboxInput
            id={inputId}
            placeholder={placeholder}
            disabled={disabled}
            showClear
          />
        )}
        <ComboboxContent>
          <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
          <ComboboxList>
            {(group, index) => (
              <ComboboxGroup key={group.key} items={group.items}>
                <ComboboxLabel>{group.label}</ComboboxLabel>
                <ComboboxCollection>
                  {(option) => (
                    <ComboboxItem
                      key={option.value}
                      value={option}
                      disabled={group.disabled}
                    >
                      {option.label}
                    </ComboboxItem>
                  )}
                </ComboboxCollection>
                {index < groups.length - 1 && <ComboboxSeparator />}
              </ComboboxGroup>
            )}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    </div>
  );
};
